import React from "react";
import { Card, CardContent } from "./ui/card";
import { Globe, Users, TrendingUp, Shield } from "lucide-react";
import useScrollAnimation from "../hooks/useScrollAnimation";
import useCounterAnimation from "../hooks/useCounterAnimation";

const StatsSection = () => {
  const [isVisible, sectionRef] = useScrollAnimation(0.2);
  const [chartVisible, chartRef] = useScrollAnimation(0.3);
  const [reachCount, reachRef] = useCounterAnimation("23M+", 2500, 200);
  const [conferencesCount, conferencesRef] = useCounterAnimation("30+", 2000, 400);
  const [countriesCount, countriesRef] = useCounterAnimation("170+", 2000, 600);
  const [executivesCount, executivesRef] = useCounterAnimation("85%", 2000, 800);

  return (
    <section 
      ref={sectionRef}
      className={`py-20 relative transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        backgroundImage: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url("https://customer-assets.emergentagent.com/job_xd-project-landing/artifacts/fkoepb5c_Exclusif_Background.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'scroll'
      }}
    >
      <div className="container mx-auto px-4">
        {/* Four Key Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {/* Reach */}
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group hover:scale-105">
            <CardContent className="p-6 text-center">
              <TrendingUp className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-xl font-bold text-yellow-500 mb-2">Reach</h3>
              <p className="text-gray-300 text-sm mb-4">
                Our content is trusted by the world's largest mining companies, global investors, and leading industry analysts.
              </p>
              <div ref={reachRef} className="text-2xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                {reachCount}
              </div>
              <div className="text-gray-400 text-xs">Monthly Views</div>
            </CardContent>
          </Card>

          {/* Exclusive */}
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group hover:scale-105">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-xl font-bold text-yellow-500 mb-2">Exclusive</h3>
              <p className="text-gray-300 text-sm mb-4">
                Exclusive access to industry leaders, mining conferences, and breaking news from across global markets.
              </p>
              <div ref={conferencesRef} className="text-2xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                {conferencesCount}
              </div>
              <div className="text-gray-400 text-xs">Annual Conferences</div>
            </CardContent>
          </Card>

          {/* Global */}
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group hover:scale-105">
            <CardContent className="p-6 text-center">
              <Globe className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-xl font-bold text-yellow-500 mb-2">Global</h3>
              <p className="text-gray-300 text-sm mb-4">
                International reach spanning North America, Europe, Asia, and rapidly expanding emerging markets worldwide.
              </p>
              <div ref={countriesRef} className="text-2xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                {countriesCount}
              </div>
              <div className="text-gray-400 text-xs">Countries Reached</div>
            </CardContent>
          </Card>

          {/* Access */}
          <Card className="bg-gray-800 border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-300 group hover:scale-105">
            <CardContent className="p-6 text-center">
              <Users className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-xl font-bold text-yellow-500 mb-2">Access</h3>
              <p className="text-gray-300 text-sm mb-4">
                Direct access to top mining executives, influential investors, and critical decision-makers from every central mining hub globally.
              </p>
              <div ref={executivesRef} className="text-2xl font-bold text-white group-hover:text-yellow-500 transition-colors">
                {executivesCount}
              </div>
              <div className="text-gray-400 text-xs">Executive Readership</div>
            </CardContent>
          </Card>
        </div>

        {/* Digital First & Trust Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            {/* Digital First */}
            <div>
              <h2 className="text-3xl font-bold text-yellow-500 mb-4">Digital First</h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our digital-first approach means your message reaches audiences where they consume content most - online, mobile, and on-demand. We leverage cutting-edge technology and data analytics to maximize your advertising ROI.
              </p>
            </div>

            {/* Trust */}
            <div>
              <h2 className="text-3xl font-bold text-yellow-500 mb-4">Trust</h2>
              <p className="text-gray-300 text-lg leading-relaxed">
                For over 45+ years, Kitco has built unparalleled trust in the mining industry. Our editorial integrity and expert analysis make us the go-to source for mining professionals worldwide.
              </p>
            </div>
          </div>

          {/* How We Stack Up Chart */}
          <div ref={chartRef} className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">How We Stack Up</h3>
            <p className="text-gray-400 text-center mb-8">Website Visitor Traffic</p>
            
            <div className="space-y-3">
              {[
                { name: "Kitco", value: 5600000, color: "bg-yellow-500", isTop: true },
                { name: "Goldprice.org", value: 1950000, color: "bg-yellow-400" },
                { name: "Bullion Vault", value: 210000, color: "bg-yellow-300" },
                { name: "Mining.com", value: 195000, color: "bg-yellow-300" },
                { name: "CEO.ca", value: 180000, color: "bg-yellow-300" },
                { name: "321Gold", value: 162000, color: "bg-yellow-200" },
                { name: "Mining Journal", value: 106000, color: "bg-yellow-200" },
                { name: "Investing News", value: 76000, color: "bg-yellow-200" },
                { name: "Junior Mining Network", value: 46000, color: "bg-yellow-200" },
                { name: "Proactive Investor", value: 45000, color: "bg-yellow-200" },
                { name: "Stockwatch", value: 43000, color: "bg-yellow-200" },
                { name: "Northern Miner", value: 16500, color: "bg-yellow-200" },
                { name: "Mining Weekly", value: 8000, color: "bg-yellow-200" },
                { name: "Australian Mining", value: 2500, color: "bg-yellow-200" },
                { name: "Mining News Network", value: 1100, color: "bg-yellow-200" }
              ].map((site, index) => {
                // Proper scaling - keep M values accurate, enhance K values but below goldprice.org
                let widthPercentage;
                
                if (site.value >= 1000000) {
                  // Sites ≥1M: Enhanced scaling to make goldprice.org more prominent (~60%)
                  const maxValueForM = 3170000; // Adjusted to make goldprice.org ~60%
                  widthPercentage = Math.min((site.value / maxValueForM) * 100, 100);
                } else {
                  // Sites <1M: Scale them proportionally but cap at max ~30% (below goldprice.org's ~35%)
                  const maxValueForK = 210000; // Highest K value
                  const minValueForK = 1100;   // Lowest value
                  
                  // Scale between 5% and 30% - all staying below goldprice.org
                  const minBarWidth = 5;
                  const maxBarWidth = 30;
                  const normalizedValue = (site.value - minValueForK) / (maxValueForK - minValueForK);
                  widthPercentage = minBarWidth + (normalizedValue * (maxBarWidth - minBarWidth));
                }
                
                return (
                  <div key={index} className="flex items-center">
                    <div className={`text-right pr-4 w-32 ${site.isTop ? 'text-yellow-500 font-bold' : 'text-gray-400'} text-sm`}>
                      {site.name}
                    </div>
                    <div className="flex-1 bg-gray-700 rounded-full h-6 relative overflow-hidden">
                      <div 
                        className={`${site.color} h-6 rounded-full transition-all duration-2000 ease-out flex items-center justify-end pr-2`}
                        style={{ 
                          width: chartVisible ? `${widthPercentage}%` : '0%',
                          transitionDelay: chartVisible ? `${index * 100}ms` : '0ms'
                        }}
                      >
                        {site.value >= 100000 && (
                          <span className={`text-black font-bold ${site.value >= 1000000 ? 'text-xs' : 'text-[10px]'}`}>
                            {site.value >= 1000000 ? `${(site.value / 1000000).toFixed(1)}M` : `${Math.round(site.value / 1000)}K`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <p className="text-gray-400 text-xs text-center mt-6">
              Source: *SimilarWeb June - August 2025 North America Web Rankings
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;